export interface WalmartStore {
  store_id: string;
  postal_code: string;
  address: string;
  geo?: { lat: number; long: number };
}

export interface HomeDepotStore {
  Id: string;
  store_number: string;
  store_title: string;
  address_1: string;
  city: string;
  state: string;
  zip: string;
  latitude: string;
  longitude: string;
}

export interface TargetStore {
  "Store No.": number;
  Name: string;
  Latitude: number;
  Longitude: number;
  Street: string;
  City: string;
  State: string;
  Zip_Code: number;
}

export interface DollarGeneralStore {
  storeNumber: string;
  address: string;
  latitude: string;
  longitude: string;
}

export interface BestBuyStore {
  store_number: string;
  name: string;
  latitude: number;
  longitude: number;
  street: string;
  city: string;
  state: string;
  zip: string;
}

export interface TractorSupplyStore {
  store_number: string;
  name: string;
  latitude: number;
  longitude: number;
  street: string;
  city: string;
  state: string;
  zip: string;
  phone?: string;
  url?: string;
}

export interface CostcoStore {
  warehouse_number: number;
  warehouse_name: string;
  region: string;
  address: string;
  location: string;
  latitude: number | null;
  longitude: number | null;
  geocodedAddress: string | null;
  timezone: string | null;
}

export type StoreType =
  | WalmartStore
  | HomeDepotStore
  | TargetStore
  | DollarGeneralStore
  | BestBuyStore
  | TractorSupplyStore
  | CostcoStore;

export interface StoreWithDistance {
  distance?: number;
  normalizedId: string;
  normalizedAddress: string;
  normalizedPostalCode: string;
  normalizedGeo?: { lat: number; long: number };
  [key: string]: unknown;
}

export const calculateDistance = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number => {
  const R = 3959;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

export const formatHomeDepotStoreId = (storeId: string | undefined | null) =>
  storeId ? storeId.replace(/^0+/, "") || storeId : "";

export const toLatLon = (x: number, y: number) => {
  const R = 6378137.0;
  return {
    lon: (x / R) * (180 / Math.PI),
    lat: (Math.atan(Math.exp(y / R)) - Math.PI / 4) * 2 * (180 / Math.PI),
  };
};

export const normalizeStore = (
  store: StoreType,
  retailer?: string
): StoreWithDistance => {
  const s = store as any;
  if (
    retailer === "bestbuy" ||
    retailer === "tractor_supply" ||
    retailer === "northern_tool" ||
    retailer === "samsclub" ||
    ("store_number" in store && "name" in store && "street" in store)
  ) {
    const b = store as BestBuyStore;
    return {
      ...store,
      normalizedId: b.store_number,
      normalizedAddress: `${b.name} - ${b.street}, ${b.city}, ${b.state}`,
      normalizedPostalCode: b.zip || "",
      normalizedGeo: { lat: b.latitude, long: b.longitude },
    };
  }
  // Costco merged format (has warehouse_number)
  if ("warehouse_number" in store) {
    const c = store as CostcoStore;
    return {
      ...store,
      normalizedId: `${c.warehouse_number}-wh`,
      normalizedAddress: `${c.address}, ${c.location}`,
      normalizedPostalCode: "",
      normalizedGeo: c.latitude && c.longitude ? { lat: c.latitude, long: c.longitude } : undefined,
    };
  }
  // Costco geocode-only format (no warehouse_number, uses address as temp ID)
  if ("geocodedAddress" in store && "timezone" in store && !("store_id" in store)) {
    const lat = typeof s.latitude === "number" ? s.latitude : parseFloat(s.latitude);
    const lon = typeof s.longitude === "number" ? s.longitude : parseFloat(s.longitude);
    return {
      ...store,
      normalizedId: s.address || "",
      normalizedAddress: s.address || s.geocodedAddress || "",
      normalizedPostalCode: "",
      normalizedGeo: !isNaN(lat) && !isNaN(lon) ? { lat, long: lon } : undefined,
    };
  }
  if (typeof s.Latitude === "number" && typeof s.Longitude === "number") {
    return {
      ...store,
      normalizedId: String(s["Store No."] || ""),
      normalizedAddress: `${s.Street || ""}, ${s.City || ""}, ${s.State || ""}`,
      normalizedPostalCode: String(s.Zip_Code || ""),
      normalizedGeo: { lat: s.Latitude, long: s.Longitude },
    };
  }
  if ("store_id" in store && "address" in store) {
    const w = store as WalmartStore;
    return {
      ...store,
      normalizedId: w.store_id,
      normalizedAddress: w.address || "Unknown Address",
      normalizedPostalCode: w.postal_code || "",
      normalizedGeo: w.geo,
    };
  }
  if ("storeNumber" in store) {
    const d = store as DollarGeneralStore;
    const lat = parseFloat(d.latitude);
    const lon = parseFloat(d.longitude);
    const postalMatch = d.address.match(/\b\d{5}(-\d{4})?\b/);
    return {
      ...store,
      normalizedId: d.storeNumber,
      normalizedAddress: d.address || "Unknown Address",
      normalizedPostalCode: postalMatch
        ? postalMatch[0].split("-")[0]
        : "",
      normalizedGeo:
        !Number.isNaN(lat) && !Number.isNaN(lon)
          ? { lat: toLatLon(lat, lon).lat, long: toLatLon(lat, lon).lon }
          : undefined,
    };
  }
  const h = store as HomeDepotStore;
  const lat = parseFloat(h.latitude);
  const lon = parseFloat(h.longitude);
  return {
    ...store,
    normalizedId: formatHomeDepotStoreId(h.store_number),
    normalizedAddress: `${h.address_1 || ""}, ${h.city || ""}, ${h.state || ""}`
      .replace(/,\s*,/g, ",")
      .replace(/^,\s*|,\s*$/g, ""),
    normalizedPostalCode: h.zip || "",
    normalizedGeo:
      !Number.isNaN(lat) && !Number.isNaN(lon)
        ? { lat, long: lon }
        : undefined,
  };
};
